import React, { useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useGameState } from '../../hooks/useGameState';
import { useTheme } from '../../hooks/useTheme';
import { useFingerprint } from '../../hooks/useFingerprint';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useFullscreen } from '../../hooks/useFullscreen';
import { useCornerTouch } from '../../hooks/useCornerTouch';
import { useLayout } from '../../hooks/useLayout';
import { useModals } from '../../hooks/useModals';
import { useDailyChallenge } from '../../hooks/useDailyChallenge';
import { useChallengeMode } from '../../hooks/useChallengeMode';
import { useCompletionModal } from '../../hooks/useCompletionModal';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useHintFeature } from '../../hooks/useHintFeature';
import { TouchDragProvider } from '../../hooks/useTouchDrag';
import { PieceOverlayProvider, PieceOverlayDisplay } from '../../hooks/usePieceOverlay';
import { useTapMode } from '../../hooks/useTapMode';
import { PuzzleBoard } from './PuzzleBoard';
import { PiecesTray } from './PiecesTray';
import { GameControls } from './GameControls';
import { PauseOverlay } from './PauseOverlay';
import { ModalCoordinator } from './ModalCoordinator';
import { CustomDragLayer } from './PuzzlePiece';
import { cn } from '../../lib/utils';

export function GameContainer() {
  // Layout hook - handles responsive sizing
  const { isMobile, isLandscape, pieceSize, currentGridSize } = useLayout();

  // Theme hook
  const { theme, setTheme } = useTheme();

  // Fingerprint hook
  const { visitorId, isLoading: isFingerprintLoading } = useFingerprint();

  // Fullscreen hook for mobile
  const { isFullscreen, isSupported: fullscreenSupported, requestFullscreen } = useFullscreen();

  // Modal management hook
  const modals = useModals();

  // Daily challenge hook
  const daily = useDailyChallenge(visitorId, currentGridSize, isFingerprintLoading);

  // Challenge mode hook
  const challenge = useChallengeMode();

  // Push notifications hook
  const pushNotifications = usePushNotifications(visitorId);

  // Tap mode hook
  const tapMode = useTapMode();

  // Hint feature hook
  const hintFeature = useHintFeature();

  // Game state hook
  const {
    imageUrl,
    difficulty,
    pieces,
    traySlots,
    board,
    moves,
    time,
    isRunning,
    isPaused,
    isComplete,
    incorrectPiece,
    hasNewImages,
    canGoBack,
    placePiece,
    swapPieces,
    removePiece,
    togglePause,
    previousImage,
    randomImage,
    playWithImage,
    changeDifficulty,
    getScore,
    markImageAsUsed,
    autoSolve,
    autoSolveWithTargetScore,
    replayPuzzle,
    gridSize,
    addPenaltyMove,
    resetIncorrectPieces,
    isFirstPlay,
  } = useGameState(visitorId, currentGridSize);

  // Hotkeys hook (hint & auto-solve)
  const { showHint, toggleHint } = useHotkeys(isComplete, autoSolve);

  // Completion modal hook
  const scoreData = isComplete ? getScore() : null;
  const {
    completionModalData,
    clearCompletionModal,
    clearForReplay,
    isCompletionModalOpen,
  } = useCompletionModal({
    isComplete,
    scoreData,
    time,
    moves,
    difficulty,
    imageUrl,
    isDailyMode: daily.isDailyMode,
    isChallengeMode: challenge.isChallengeMode,
    challengeCode: challenge.currentChallengeCode,
    dailyPuzzleDate: daily.dailyPuzzleDate,
    gridSize,
    hintPenalty: hintFeature.hintPenalty,
  });

  // Corner touch hook for secret menu (mobile only)
  const handleSecretMenuActivate = useCallback(() => {
    modals.openSecretMenu();
  }, [modals]);

  const {
    onTouchStart: cornerTouchStart,
    onTouchMove: cornerTouchMove,
    onTouchEnd: cornerTouchEnd,
    reset: resetCornerTouch,
  } = useCornerTouch({
    holdDuration: 1000,
    cornerThreshold: 50,
    onActivate: handleSecretMenuActivate,
  });

  // Auto-enable fullscreen on first user interaction (required by browsers)
  useEffect(() => {
    if (!isMobile || !fullscreenSupported || isFullscreen) return;

    const handleFirstInteraction = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        requestFullscreen();
      }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [isMobile, fullscreenSupported, isFullscreen, requestFullscreen]);

  // Handlers
  const handlePlayDaily = useCallback((imageUrl, puzzleDate) => {
    daily.startDailyChallenge(imageUrl, puzzleDate);
    changeDifficulty('normal');
    markImageAsUsed(imageUrl);
    playWithImage(imageUrl);
  }, [daily, changeDifficulty, playWithImage, markImageAsUsed]);

  const handleDailySaveAndShare = useCallback((savedScore) => {
    daily.saveDailyScore(savedScore);
    clearCompletionModal();
    randomImage();
  }, [daily, clearCompletionModal, randomImage]);

  const handleRegularNewPuzzle = useCallback(() => {
    clearCompletionModal();
    daily.exitDailyMode();
    challenge.exitChallengeMode();
    hintFeature.resetHints();
    randomImage();
  }, [clearCompletionModal, daily, challenge, hintFeature, randomImage]);

  const handleReplayPuzzle = useCallback(() => {
    clearForReplay();
    daily.exitDailyMode();
    challenge.exitChallengeMode();
    hintFeature.resetHints();
    replayPuzzle();
  }, [clearForReplay, daily, challenge, hintFeature, replayPuzzle]);

  const handlePlayChallenge = useCallback((challengeImageUrl, challengeDifficulty, challengeCode) => {
    clearCompletionModal();
    modals.closeDailyPrompt();
    challenge.startChallenge(challengeCode);
    changeDifficulty(challengeDifficulty);
    markImageAsUsed(challengeImageUrl);
    playWithImage(challengeImageUrl);
  }, [clearCompletionModal, modals, challenge, changeDifficulty, playWithImage, markImageAsUsed]);

  const handleChallengeNewPuzzle = useCallback(() => {
    clearCompletionModal();
    challenge.exitChallengeMode();
    hintFeature.resetHints();
    randomImage();
  }, [clearCompletionModal, challenge, hintFeature, randomImage]);

  // Hint feature handlers
  const handleHintRequest = useCallback(() => {
    if (isComplete || isPaused) return;
    hintFeature.requestHint();
  }, [isComplete, isPaused, hintFeature]);

  const handleHintConfirm = useCallback(() => {
    hintFeature.confirmHint(traySlots, board);
  }, [hintFeature, traySlots, board]);

  // Touch handlers for mobile drag
  const handleTouchDrop = useCallback((pieceId, row, col, fromTray, sourceRow, sourceCol) => {
    placePiece(pieceId, row, col, fromTray, sourceRow, sourceCol);
  }, [placePiece]);

  const handleTouchSwap = useCallback((pieceId, sourceRow, sourceCol, targetRow, targetCol, fromTray) => {
    swapPieces(pieceId, sourceRow, sourceCol, targetRow, targetCol, fromTray);
  }, [swapPieces]);

  // Determine if any modal/overlay is open
  const isAnyModalOpen =
    modals.isAnyModalOpen ||
    challenge.showChallengeInfo ||
    isCompletionModalOpen ||
    (isPaused && isRunning);

  return (
    <DndProvider backend={HTML5Backend}>
      <TouchDragProvider onDrop={handleTouchDrop} onSwap={handleTouchSwap} onReturnToTray={removePiece} pieceSize={pieceSize}>
      <PieceOverlayProvider imageUrl={imageUrl} pieceSize={pieceSize} gridSize={gridSize} onPenaltyMove={addPenaltyMove}>
      <div className={cn(
        "min-h-screen min-h-[100dvh] bg-background flex flex-col overflow-y-auto",
        isMobile ? "game-container-mobile safe-area-bottom" : "game-container-desktop"
      )}>
        <CustomDragLayer />
        <PieceOverlayDisplay />

        {/* Fixed Stats Bar with Controls */}
        <GameControls
          time={time}
          moves={moves}
          difficulty={difficulty}
          onDifficultyChange={changeDifficulty}
          isPaused={isPaused}
          isRunning={isRunning}
          onPause={togglePause}
          onPrevious={previousImage}
          onRandom={randomImage}
          hasNewImages={hasNewImages}
          canGoBack={canGoBack}
          onReset={resetIncorrectPieces}
          onSettings={modals.openSettings}
          onLeaderboard={modals.openLeaderboard}
          onDailyChallenge={modals.openDailyChallenge}
          onChallengeFriend={modals.openAvailableChallenges}
          onHistory={modals.openHistory}
          onHelp={modals.openHelp}
          hasPlayedDailyToday={daily.hasPlayedDailyToday}
          imageUrl={imageUrl}
          notificationCount={pushNotifications.notificationCount}
          notifications={pushNotifications.allNotifications}
          onMarkNotificationsRead={pushNotifications.markAsRead}
          onViewChallengeNotification={() => modals.openAvailableChallenges()}
          isLandscape={isLandscape}
          isMobile={isMobile}
          hideReferenceImage={isAnyModalOpen}
          onHint={handleHintRequest}
          hasUnplacedPieces={pieces.length > 0}
          isComplete={isComplete}
          hintPenalty={hintFeature.hintPenalty}
        />

        {/* Spacer for fixed header (account for iOS status bar / Dynamic Island via safe-area-inset-top) */}
        <div
          className={cn(
            !isMobile && "h-[120px]",
            isMobile && !isLandscape && "h-[88px] sm:h-[92px]"
          )}
          style={{
            ...(isMobile && isLandscape ? { height: '42px' } : {}),
            // Add the iOS safe-area-inset-top to the spacer so content below the
            // fixed header is not hidden behind it on iPhones with a notch/Dynamic Island.
            paddingTop: 'env(safe-area-inset-top)',
          }}
          data-testid="header-spacer"
        />

        {/* Main Content */}
        <main className={cn(
          "flex-1 container mx-auto px-2 sm:px-4 py-2 sm:py-4 max-w-full overflow-y-auto",
          isLandscape && "py-0 px-2",
          !isMobile && "max-w-6xl py-6"
        )}>
          {/* Desktop Layout - Side by Side */}
          {!isMobile && (
            <div className="flex gap-8 justify-center items-start">
              <div className="flex flex-col items-center gap-3">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Pieces</h2>
                <PiecesTray
                  pieces={pieces}
                  traySlots={traySlots}
                  pieceSize={pieceSize}
                  isMobile={false}
                  gridSize={gridSize}
                  onReturnToTray={removePiece}
                  useTapMode={tapMode.useTapMode}
                  selectedPiece={tapMode.selectedPiece}
                  onPieceTap={tapMode.selectPiece}
                  onTrayTap={() => tapMode.handleTrayTap(removePiece)}
                  isPieceSelected={tapMode.isPieceSelected}
                  hintPieceId={hintFeature.hintPieceId}
                />
              </div>

              <div className="flex flex-col items-center gap-3">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Puzzle Board</h2>
                <PuzzleBoard
                  board={board}
                  pieceSize={pieceSize}
                  onDrop={placePiece}
                  onSwap={swapPieces}
                  onRemove={removePiece}
                  incorrectPiece={incorrectPiece}
                  imageUrl={imageUrl}
                  difficulty={difficulty}
                  showHint={showHint}
                  isMobile={false}
                  gridSize={gridSize}
                  useTapMode={tapMode.useTapMode}
                  selectedPiece={tapMode.selectedPiece}
                  onPieceTap={tapMode.selectPiece}
                  onCellTap={(row, col, hasPiece, isCorrect) =>
                    tapMode.handleBoardCellTap(row, col, hasPiece, isCorrect, placePiece, swapPieces)
                  }
                  isPieceSelected={tapMode.isPieceSelected}
                  hintTargetCell={hintFeature.hintTargetCell}
                />
              </div>
            </div>
          )}

          {/* Mobile Portrait Layout */}
          {isMobile && !isLandscape && (
            <div className="flex flex-col gap-2 pt-2 items-center w-full max-w-full">
              <PiecesTray
                pieces={pieces}
                traySlots={traySlots}
                pieceSize={pieceSize}
                isMobile={true}
                gridSize={gridSize}
                onReturnToTray={removePiece}
                useTapMode={tapMode.useTapMode}
                selectedPiece={tapMode.selectedPiece}
                onPieceTap={tapMode.selectPiece}
                onTrayTap={() => tapMode.handleTrayTap(removePiece)}
                isPieceSelected={tapMode.isPieceSelected}
                hintPieceId={hintFeature.hintPieceId}
              />
              <PuzzleBoard
                board={board}
                pieceSize={pieceSize}
                onDrop={placePiece}
                onSwap={swapPieces}
                onRemove={removePiece}
                incorrectPiece={incorrectPiece}
                imageUrl={imageUrl}
                difficulty={difficulty}
                showHint={showHint}
                isMobile={true}
                gridSize={gridSize}
                onCornerTouchStart={cornerTouchStart}
                onCornerTouchMove={cornerTouchMove}
                onCornerTouchEnd={cornerTouchEnd}
                useTapMode={tapMode.useTapMode}
                selectedPiece={tapMode.selectedPiece}
                onPieceTap={tapMode.selectPiece}
                onCellTap={(row, col, hasPiece, isCorrect) =>
                  tapMode.handleBoardCellTap(row, col, hasPiece, isCorrect, placePiece, swapPieces)
                }
                isPieceSelected={tapMode.isPieceSelected}
                hintTargetCell={hintFeature.hintTargetCell}
              />
            </div>
          )}

          {/* Mobile Landscape Layout */}
          {isMobile && isLandscape && (
            <div className="flex gap-2 justify-center items-center" style={{ maxHeight: 'calc(100vh - 50px)' }}>
              <PiecesTray
                pieces={pieces}
                traySlots={traySlots}
                pieceSize={pieceSize}
                isMobile={true}
                isLandscape={true}
                gridSize={gridSize}
                onReturnToTray={removePiece}
                useTapMode={tapMode.useTapMode}
                selectedPiece={tapMode.selectedPiece}
                onPieceTap={tapMode.selectPiece}
                onTrayTap={() => tapMode.handleTrayTap(removePiece)}
                isPieceSelected={tapMode.isPieceSelected}
                hintPieceId={hintFeature.hintPieceId}
              />
              <PuzzleBoard
                board={board}
                pieceSize={pieceSize}
                onDrop={placePiece}
                onSwap={swapPieces}
                onRemove={removePiece}
                incorrectPiece={incorrectPiece}
                imageUrl={imageUrl}
                difficulty={difficulty}
                showHint={showHint}
                isMobile={true}
                isLandscape={true}
                gridSize={gridSize}
                onCornerTouchStart={cornerTouchStart}
                onCornerTouchMove={cornerTouchMove}
                onCornerTouchEnd={cornerTouchEnd}
                useTapMode={tapMode.useTapMode}
                selectedPiece={tapMode.selectedPiece}
                onPieceTap={tapMode.selectPiece}
                onCellTap={(row, col, hasPiece, isCorrect) =>
                  tapMode.handleBoardCellTap(row, col, hasPiece, isCorrect, placePiece, swapPieces)
                }
                isPieceSelected={tapMode.isPieceSelected}
                hintTargetCell={hintFeature.hintTargetCell}
              />
            </div>
          )}
        </main>

        <PauseOverlay isPaused={isPaused && isRunning} onResume={togglePause} />

        {/* All modals are coordinated by a single component */}
        <ModalCoordinator
          visitorId={visitorId}
          imageUrl={imageUrl}
          difficulty={difficulty}
          time={time}
          moves={moves}
          scoreData={scoreData}
          gridSize={gridSize}
          isComplete={isComplete}
          isFirstPlay={isFirstPlay}
          playWithImage={playWithImage}
          toggleHint={toggleHint}
          autoSolveWithTargetScore={autoSolveWithTargetScore}
          autoSolve={autoSolve}
          resetCornerTouch={resetCornerTouch}
          completionModalData={completionModalData}
          clearCompletionModal={clearCompletionModal}
          clearForReplay={clearForReplay}
          handleRegularNewPuzzle={handleRegularNewPuzzle}
          handleReplayPuzzle={handleReplayPuzzle}
          handleChallengeNewPuzzle={handleChallengeNewPuzzle}
          handlePlayChallenge={handlePlayChallenge}
          handlePlayDaily={handlePlayDaily}
          handleDailySaveAndShare={handleDailySaveAndShare}
          handleHintConfirm={handleHintConfirm}
          modals={modals}
          daily={daily}
          challenge={challenge}
          hintFeature={hintFeature}
          pushNotifications={pushNotifications}
          theme={theme}
          setTheme={setTheme}
          tapMode={tapMode}
          isMobile={isMobile}
          fullscreenSupported={fullscreenSupported}
          isFullscreen={isFullscreen}
          requestFullscreen={requestFullscreen}
        />
      </div>
      </PieceOverlayProvider>
      </TouchDragProvider>
    </DndProvider>
  );
}
